import { DataAccess } from "../dataaccess/data.access";
import { EventService } from "./event.service";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { Timeout } from "../decorators/timeout.decorator";
import {
    Attribute,
    EventDataMicroI,
    EventListWsDataI,
    EventMicroI,
    PutEventMircoListResponse,
    Role,
    WsDataI,
} from "../modules/s25-event/EventMicroI";
import { S25Util } from "../util/s25-util";
import { Requirement } from "../modules/s25-event/Event";
import { Event } from "../pojo/Event";

export type EventIncludes =
    | "attributes"
    | "categories"
    | "context"
    | "organizations"
    | "reservations"
    | "requirements"
    | "roles"
    | "text"
    | "all"
    | "registration";
export class EventMicroService {
    @Timeout
    @Cache({ immutable: true, targetName: "EventMicroService" })
    public static getEventDetailById(ids: (number | null | undefined)[], expand?: boolean, includes?: EventIncludes[]) {
        const validIds = S25Util.array.validNumbers(ids);
        if (!validIds.length) {
            return Promise.resolve<{ content: WsDataI }>({ content: { data: { items: [] } } });
        }
        const params = DataAccess.params({
            expand: expand ? "T" : "F",
            ...(includes?.length ? { include: includes } : {}),
            caller: "EventMicroService.getEventDetailById",
        });
        const url = `/micro/event/${validIds.join("+")}/detail.json?${params}`;
        return DataAccess.get<{ content: WsDataI }>(url);
    }

    @Timeout
    public static getEvents(urlParams: Event.WsQueryParams) {
        const queryString = DataAccess.params(urlParams);
        const url = `/micro/event/list.json?${queryString}`;
        return DataAccess.get<{ content: EventListWsDataI }>(
            DataAccess.injectCaller(url, "EventMicroService.getEvents"),
        );
    }

    @Invalidate({ serviceName: "EvDetailService", methodName: "get", patternFunc: (args) => args[0] })
    @Invalidate({ serviceName: "EventMicroService", methodName: "getEventDetailById", patternFunc: (args) => args[0] })
    public static async microPutEventDetail(data: EventDataMicroI, eventId: number) {
        const etags = await EventService.getEtags([eventId]);
        return DataAccess.put(
            DataAccess.injectCaller(
                "/micro/event/" + eventId + "/detail.json?expand=T&include=All",
                "EventMicroService.microPutEventDetail",
            ),
            {
                content: {
                    etags: etags,
                    data: data,
                },
            },
        );
    }

    // for reference https://src.unival.com/projects/database/browser/database/SERIES25/accelerator/s25ws-db/sample_event_reservation_json.json
    @Invalidate({ serviceName: "EvDetailService", methodName: "get", patternFunc: (args) => args[0] })
    @Invalidate({ serviceName: "EventMicroService", methodName: "getEventDetailById", patternFunc: (args) => args[0] })
    public static async putEventReservation(eventId: number, data: EventDataMicroI) {
        const etags = await EventService.getEtags([eventId]);
        return DataAccess.put(
            DataAccess.injectCaller(
                "/micro/event/" + eventId + "/reservation.json?expand=T&include=All",
                "EventMicroService.putEventReservation",
            ),
            {
                content: {
                    apiVersion: "0.1",
                    etags: etags,
                    data: data,
                },
            },
        );
    }

    public static async putList(data: EventMicroI[]) {
        const url = "/micro/event/list.json";
        const injected = DataAccess.injectCaller(url, "EventMicroService.putList");
        const promise = DataAccess.put<{ content: PutEventMircoListResponse }>(injected, { content: { data } }).then(
            (data) => data?.content,
        );
        return S25Util.Result<
            PutEventMircoListResponse,
            { data: { content: Pick<PutEventMircoListResponse, "error"> } }
        >(promise);
    }

    public static async putEventRequirements(data: Requirement[], eventId: number, isRemove?: boolean) {
        let reqObj: any = { requirements: data };
        if (isRemove) reqObj = { remove: reqObj };
        const payload = {
            content: {
                data: [reqObj],
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller(
                "/micro/event/" + eventId + "/requirement.json",
                "EventMicroService.putEventRequirements",
            ),
            payload,
        );
    }

    public static async putEventAttributes(data: Attribute[], eventId: number) {
        const payload = {
            content: {
                data: data,
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller(
                "/micro/event/" + eventId + "/attribute.json",
                "EventMicroService.putEventAttributes",
            ),
            payload,
        );
    }

    public static async putEventRoles(data: Role[], eventId: number, isRemove?: boolean) {
        let rolesObj: any = { roles: data };
        if (isRemove) rolesObj = { remove: rolesObj };
        const payload = {
            content: {
                data: rolesObj,
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller("/micro/event/" + eventId + "/role.json", "EventMicroService.putEventRoles"),
            payload,
        );
    }
}
